import { useLazyQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { deleteAudioChunkRecordsForRecording } from 'woundscribe/aural/audioChunkDb';
import {
  INITIAL_STATUS_CHECK_DELAY_MS,
  MAX_STATUS_CHECK_RETRIES,
} from 'woundscribe/core/constants';
import { useLastCompletedStop } from 'woundscribe/core/recordingStore';
import { delay } from 'woundscribe/core/utils';
import { WSAudioStorageQuery } from 'woundscribe/Schema';

/**
 * Resolves true once the server reports `canBeDeleted`. Resolves false when the
 * polls run out or the caller has cancelled. A failed check counts as "not yet".
 */
export async function waitUntilDeletable(
  sessionId: string,
  isDeletable: (sessionId: string) => Promise<boolean>,
  isCancelled: () => boolean
): Promise<boolean> {
  await delay(INITIAL_STATUS_CHECK_DELAY_MS);
  for (let attempt = 0; attempt <= MAX_STATUS_CHECK_RETRIES; attempt++) {
    if (isCancelled()) {
      return false;
    }
    if (await isDeletable(sessionId)) {
      return true;
    }
    if (attempt < MAX_STATUS_CHECK_RETRIES) {
      await delay(INITIAL_STATUS_CHECK_DELAY_MS * 2 ** attempt);
    }
  }
  return false;
}

/**
 * Deletes a stopped recording's local audio once the server confirms it has the
 * audio. Each stop finishing starts one poll. Mount it once, from a component that
 * is always rendered (the footer).
 *
 * Local chunks are deleted only after `canBeDeleted` is confirmed. If the poll
 * gives up or the footer unmounts, the chunks stay in IndexedDB for the recovery flow.
 */
export function useAudioEviction(): void {
  const lastCompletedStop = useLastCompletedStop();
  const [checkAudioStorageStatus] = useLazyQuery(WSAudioStorageQuery, {
    fetchPolicy: 'network-only',
  });

  const isMountedRef = useRef(true);
  const checkRef = useRef(checkAudioStorageStatus);
  checkRef.current = checkAudioStorageStatus;
  // The value present at mount belongs to an earlier stop, so it is not evicted here.
  const handledStopRef = useRef(lastCompletedStop);

  // Cancellation is tied to unmount only.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const isDeletable = async (sessionId: string): Promise<boolean> => {
      try {
        const { data } = await checkRef.current({ variables: { sessionId } });
        return Boolean(data?.recordingUploadStatus?.canBeDeleted);
      } catch {
        return false;
      }
    };

    const evictWhenDeletable = async (
      localRecordingId: string,
      sessionId: string
    ) => {
      const isCancelled = () => !isMountedRef.current;
      const deletable = await waitUntilDeletable(
        sessionId,
        isDeletable,
        isCancelled
      );
      if (!deletable || isCancelled()) {
        return;
      }
      try {
        await deleteAudioChunkRecordsForRecording(localRecordingId);
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: reason here
        console.warn(
          '[WoundScribe] Failed to evict confirmed audio chunks:',
          err instanceof Error ? err.message : err
        );
      }
    };

    if (lastCompletedStop === handledStopRef.current) {
      return;
    }
    handledStopRef.current = lastCompletedStop;

    const localRecordingId = lastCompletedStop?.localRecordingId;
    const backendSessionId = lastCompletedStop?.backendSessionId;
    // Without both ids the server cannot confirm the audio, so the chunks stay.
    if (!localRecordingId || !backendSessionId) {
      return;
    }
    void evictWhenDeletable(localRecordingId, backendSessionId);
  }, [lastCompletedStop]);
}
